import { Button, HTMLChakraProps, MenuButton } from '@chakra-ui/react';
import { useResourceRelationship } from 'datx-jsonapi-react';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import FilterList from '@/assets/icons/ic-filter-list.svg';

import { useSearchState } from '@/components/ui/core/Search/Search';
import { searchGroups } from '@/components/ui/features/schedule/ScheduleSearchResult/constants';
import { DropdownSelect, IOption } from '@/components/ui/shared/DropdownSelect/DropdownSelect';
import { SearchInput } from '@/components/ui/shared/inputs/SearchInput/SearchInput';
import { SearchGroup } from '@/enums';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { queryThinEventConfigurationRelationship } from '@/queries/thin-events';
import { EventConfiguration } from '@/resources/EventConfiguration';

const ALL_SEARCH_GROUP_VALUE = 'all';

const defaultValue = {
  id: ALL_SEARCH_GROUP_VALUE,
  value: ALL_SEARCH_GROUP_VALUE,
};

const items = [defaultValue, ...searchGroups];

interface IUrlQueryStringParams {
  searchGroup?: string;
  query?: string;
}

export const ScheduleSearchInput: FC<HTMLChakraProps<'div'>> = (props) => {
  const { t } = useTranslation();
  const { getSearchInputProps } = useSearchState();

  const { eventId } = useQueryParams<{ eventId: string }>({
    eventId: StringParam,
  });

  const { data: eventConfiguration } = useResourceRelationship<EventConfiguration>(
    queryThinEventConfigurationRelationship(eventId)
  );

  const { searchGroup, change, query } = useQueryParams<IUrlQueryStringParams>({
    searchGroup: StringParam,
    query: StringParam,
  });

  const selectedOption = useMemo(() => items.find((item) => item.id === searchGroup), [searchGroup]);

  const onChange = useCallback(
    (item: IOption) => {
      const searchGroupParams = item.id !== ALL_SEARCH_GROUP_VALUE ? item.id : null;

      change({ searchGroup: searchGroupParams }, { method: 'replace', shallow: true });
    },
    [change]
  );
  const searchInputProps = getSearchInputProps();

  const placeholder =
    !selectedOption || selectedOption.value.toLowerCase() === SearchGroup.ALL
      ? t('eventSessionsList.searchPlaceholder', {
          talks: eventConfiguration?.lecturesDropdownLabel?.toLowerCase() || 'talks',
          sessions: eventConfiguration?.sessionsLabelName?.toLowerCase() || 'sessions',
          posters: eventConfiguration?.postersDropdownLabel?.toLowerCase() || 'posters',
        })
      : `search ${selectedOption.value.toLowerCase()}`;

  const lectureItem = searchGroups.find((i) => i.id === SearchGroup.LECTURE);
  const sessionItem = searchGroups.find((i) => i.id === SearchGroup.EVENT_SESSION);
  const posterItem = searchGroups.find((i) => i.id === SearchGroup.POSTER);

  lectureItem.value = eventConfiguration.lecturesDropdownLabel || 'Lectures';
  sessionItem.value = eventConfiguration.sessionsLabelName || 'Sessions';
  posterItem.value = eventConfiguration.postersDropdownLabel || 'Posters';

  return (
    <SearchInput
      {...props}
      {...searchInputProps}
      actionSlot={
        <DropdownSelect
          options={items}
          menuButtonSlot={
            <MenuButton as={Button} variant="link" color="gray.200" rightIcon={<FilterList />} boxSize={10} />
          }
          onClickOption={onChange}
          placement={'bottom-end'}
          selectedOption={selectedOption?.value ? selectedOption : items[0]}
        />
      }
      placeholder={placeholder}
    />
  );
};
